import { useEffect, useMemo, useState } from 'react';

import { useRouter } from 'next/router';

import { useAsset } from '~/techstyle-shared/react-assets';
import { useBreakpoint } from '~/techstyle-shared/react-components';

import { MOCK_IN_GRID_ASSETS } from '../__mocks__/mockInGridAssets';
import {
  getEligibleAssets,
  getMergedProductsAndAssets,
} from '../utils/inGridAssets';

import useGridInputs from './useGridInputs';
import useNumGridCols from './useNumGridCols';

const EMPTY_ASSETS = [];

const useInGridAssets = ({ products = [] }) => {
  const { asPath, isReady } = useRouter();
  const [igaOverride, setIgaOverride] = useState(null);
  const numGridCols = useNumGridCols();
  const { isMobile } = useBreakpoint();

  // Handle IGA (In-Grid Asset) override from query params
  useEffect(() => {
    if (!isReady) {
      return;
    }

    const search = asPath.split('?')[1] || '';
    const nextOverride = new URLSearchParams(search).get('iga_override');

    setIgaOverride(nextOverride ?? null);
  }, [asPath, isReady]);

  const { assetContainer } = useGridInputs();
  const gridAssetContainerDesktop = assetContainer?.gridAssetContainer;
  const gridAssetContainerMobile =
    assetContainer?.gridAssetContainerMobile || gridAssetContainerDesktop;
  const gridAssetContainer = isMobile
    ? gridAssetContainerMobile
    : gridAssetContainerDesktop;

  // Needs to be a valid override container, eg, 'CPTest_Grid_Banner_1'
  const overrideContainer = igaOverride && igaOverride !== 'dev_mock';
  const activeContainer = overrideContainer ? igaOverride : gridAssetContainer;
  const gridAssetContainerObj = useAsset(activeContainer, {
    multi: true,
  });

  // If igaOverride is 'dev_mock', use MOCK_IN_GRID_ASSETS
  const assets =
    igaOverride === 'dev_mock'
      ? MOCK_IN_GRID_ASSETS
      : gridAssetContainerObj?.data?.assets || EMPTY_ASSETS;

  // Memoize the asset eligibility pass — assets/numGridCols change only on
  // data load or breakpoint flip, so recomputing on every parent re-render
  // is wasted work.
  const { assetCellReservationMap, eligibleAssets } = useMemo(
    () => getEligibleAssets({ assets, numGridCols }),
    [assets, numGridCols]
  );

  // Memoize the merge. Previously this returned a new array reference on
  // every render, which forced VirtualScrollGrid to derive a content
  // signature to keep its row-height cache stable. With a stable reference
  // downstream consumers can rely on identity checks directly.
  const mergedAssetsAndProducts = useMemo(
    () =>
      getMergedProductsAndAssets({
        products,
        numGridCols,
        eligibleAssets,
        assetCellReservationMap,
      }),
    [products, numGridCols, eligibleAssets, assetCellReservationMap]
  );

  return {
    inGridAssets: eligibleAssets,
    assetCellReservationMap,
    mergedAssetsAndProducts,
  };
};

export default useInGridAssets;
